// Help article content organized by category
// Each article has SEO-optimized title, description, and comprehensive content

export interface HelpArticle {
  slug: string;
  title: string;
  description: string;
  content: string;
  relatedArticles?: { category: string; slug: string; title: string }[];
}

export interface HelpCategory {
  id: string;
  title: string;
  description: string;
  articles: HelpArticle[];
}

export const helpArticles: Record<string, HelpCategory> = {
  "getting-started": {
    id: "getting-started",
    title: "Getting Started",
    description: "New to SurviAI? Start here to learn the basics.",
    articles: [
      {
        slug: "first-survey",
        title: "Creating Your First Survey",
        description: "Learn how to create your first digital survey with SurviAI in just a few minutes using our AI-powered document conversion.",
        content: `
## Creating Your First Survey

Welcome to SurviAI! This guide will walk you through creating your first digital survey using our AI-powered platform.

### Step 1: Prepare Your Document

Before uploading, make sure your survey document is in one of these supported formats:

- **PDF** (.pdf) - Best for scanned surveys
- **Word Document** (.doc or .docx) - Good for text-based surveys
- **Excel Spreadsheet** (.xls or .xlsx) - Great for structured data
- **Image** (.jpg, .jpeg, or .png) - For scanned paper surveys

**Maximum file size:** 10MB

### Step 2: Upload Your Document

On your Home Page, find the upload area. You have two options:

1. **Drag and Drop**: Simply drag your file from your computer and drop it into the dotted upload box
2. **Click to Browse**: Click the upload area and select your file from your computer

### Step 3: Wait for AI Processing

After uploading, our AI will analyze your document. You'll see a progress indicator showing:

- ⏳ Uploading file...
- ⏳ Analyzing document...
- ⏳ Extracting questions...
- ⏳ Detecting skip logic and routing...
- ✅ Ready to create survey!

**Processing time:** Usually 1-5 minutes depending on document length.

**What the AI extracts automatically:**
- Questions and answer options
- Question types (single choice, multiple choice, etc.)
- Skip logic (e.g., "If No, go to Q5")
- Display conditions (e.g., "Ask only if Q2 = Yes")
- Quota instructions
- Required questions
- Randomization instructions

### Step 4: Create Your Survey

Once processing is complete:

1. You'll see a "Ready" status with a green badge
2. Click the **"Create Survey"** button
3. Your survey will be created and you'll be taken to the Survey Editor

### Step 5: Review and Edit in the Survey Editor

The Survey Editor opens with your AI-extracted questions. Here you can:

- **Review** each question for accuracy
- **Edit** question text and answer options
- **Reorder** questions by dragging the handle (⋮⋮)
- **Change question types** using the dropdown
- **Add skip logic** and display conditions
- **Delete** questions that weren't extracted correctly
- **Add new questions** manually if needed

The editor shows a live preview on the right side so you can see exactly how respondents will experience your survey.

### Step 6: Publish Your Survey

When you're satisfied with your survey:

1. Look at the right panel for the **"Publish Survey"** button (purple with rocket icon 🚀)
2. Click the button
3. Wait for the confirmation: "🎉 Survey Published!"
4. Copy your unique share link from the green "Survey is Live!" box

### Creating a Survey from Scratch

Don't have a document? You can still create a survey manually:

1. Upload any document to start the process
2. After AI processing, click "Create Survey"
3. In the Survey Editor, delete the AI-generated questions
4. Click **"Add New Question"** to build your survey from scratch

### Tips for Success

- Keep surveys focused and concise
- Use clear, simple language
- Test your survey using "Open Full Preview" before publishing
- Consider mobile respondents - all SurviAI surveys are mobile-friendly
- Review all skip logic paths to ensure respondents flow through correctly

You're now ready to collect responses!
        `,
        relatedArticles: [
          { category: "getting-started", slug: "dashboard-overview", title: "Understanding the Dashboard" },
          { category: "getting-started", slug: "upload-document", title: "Uploading Your First Document" },
          { category: "survey-creation", slug: "question-types", title: "Question Types Overview" },
        ],
      },
      {
        slug: "dashboard-overview",
        title: "Understanding the Dashboard",
        description: "Navigate the SurviAI dashboard like a pro. Learn about all the features and tools available to manage your surveys.",
        content: `
## Understanding the Dashboard

SurviAI has two main views: your **Home Page** and the **Projects Page**. Here's what you'll find in each.

### Home Page

After logging in, you'll land on your Home Page. This is where you start new surveys.

**Top Section: Welcome Area**
- A message explaining what SurviAI does
- Quick stats about the system

**Middle Section: Upload Area**
- Where you upload documents to convert into surveys
- Drag and drop or click to browse for files
- Shows processing status for uploaded documents

**Bottom Section: Recent Projects**
- Your recently created surveys for quick access
- Click any project to open it in the editor

### Navigation Bar

The top navigation provides access to:

- **SurviAI Logo** - Click to go to your home page
- **Help** - Access documentation and support
- **Your Profile** - Access settings and log out

### Projects Page

Access all your surveys in one place:

1. Click your profile in the top-right
2. Select **"Projects"** from the dropdown

**Create New Survey Section**
- Upload button to start a new survey
- Processing jobs awaiting action

**My Projects Section**
- All your existing surveys
- Filter and search options
- Grid or Table view toggle

### Survey Status Badges

| Badge | Meaning |
|-------|---------|
| 🟠 **Ready** | AI processed, ready to create survey |
| 🟡 **Draft** | Survey created but not published |
| 🟣 **Published** | Survey is live and accepting responses |
| 🟢 **Quotas** | Survey has quota tracking enabled |
| ⚪ **Archived** | Survey is closed |

### Filtering Surveys

Use the filter dropdown to show:
- **All Status**: Everything
- **Ready to Create**: Processed documents
- **Draft**: Unpublished surveys
- **Published**: Live surveys
- **Archived**: Closed surveys

### View Options

Switch between views:
- **Grid View** (📊): Shows surveys as cards
- **Table View** (📋): Shows surveys in a list

### Survey Actions

Each survey has action buttons:

| Icon | Action |
|------|--------|
| ✏️ (Edit) | Open in Survey Editor |
| 🔗 (Link) | Copy share link (published only) |
| 👥 (Users) | View responses (published only) |
| 📋 (Clone) | Create a copy of this survey |
| 🔍 (Preview) | Preview live survey |
| 📊 (Quotas) | View quota dashboard (if enabled) |
| 🗑️ (Delete) | Remove survey permanently |

### Tips for Organization

- Use descriptive survey titles
- Add descriptions for context
- Archive completed surveys when done
- Use consistent naming conventions
        `,
        relatedArticles: [
          { category: "getting-started", slug: "first-survey", title: "Creating Your First Survey" },
          { category: "getting-started", slug: "account-setup", title: "Account Setup and Preferences" },
        ],
      },
      {
        slug: "upload-document",
        title: "Uploading Your First Document",
        description: "Step-by-step guide to uploading documents for AI-powered survey extraction. Learn about supported formats and best practices.",
        content: `
## Uploading Your First Document

SurviAI's AI can automatically extract survey questions from your existing documents. Here's how to get started.

### Supported File Formats

We support a wide range of document formats:

- **PDF** (.pdf) - Scanned or digital
- **Microsoft Word** (.doc, .docx)
- **Microsoft Excel** (.xls, .xlsx)
- **Images** (.png, .jpg, .jpeg) - Uses OCR

**Maximum file size:** 10MB

### How to Upload

On your **Home Page**, find the upload area:

1. **Drag and Drop**: Simply drag your file from your computer and drop it into the dotted upload box
2. **Click to Browse**: Click the upload area and select your file from your computer

### What Happens Next

After uploading, our AI processes your document. You'll see a progress indicator showing:

- ⏳ Uploading file...
- ⏳ Analyzing document...
- ⏳ Extracting questions...
- ⏳ Detecting skip logic and routing...
- ✅ Ready to create survey!

**Processing time:** Usually 1-5 minutes depending on document length.

### What the AI Extracts Automatically

- Questions and answer options
- Question types (single choice, multiple choice, etc.)
- Skip logic (e.g., "If No, go to Q5")
- Display conditions (e.g., "Ask only if Q2 = Yes")
- Quota instructions
- Required questions
- Randomization instructions

### After Processing

Once processing is complete:

1. You'll see a **"Ready"** status with a green badge
2. Click the **"Create Survey"** button
3. Your survey opens in the Survey Editor for review

### Tips for Best Results

**Document Quality**
- Use clear, high-resolution scans (300 DPI+)
- Ensure text is readable
- Avoid handwritten content

**Formatting**
- Numbered questions work best
- Clear separation between questions
- Consistent formatting throughout

**Content**
- One survey per document
- Include answer options where applicable
- Explicit skip logic references (e.g., "If No, skip to Q5")

### Troubleshooting

**No questions detected?**
- Check document quality
- Ensure questions are clearly formatted
- Try a different file format

**Wrong question types?**
- You can manually change types in the Survey Editor
- Provide clearer answer options in source

**Missing content?**
- Check if text is selectable in original PDF
- For scanned documents, ensure good contrast
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "supported-formats", title: "Supported Document Formats" },
          { category: "survey-creation", slug: "ai-extraction", title: "AI Question Extraction Explained" },
          { category: "troubleshooting", slug: "upload-issues", title: "Document Upload Issues" },
        ],
      },
      {
        slug: "account-setup",
        title: "Account Setup and Preferences",
        description: "Configure your SurviAI account settings, preferences, and profile information for the best experience.",
        content: `
## Account Setup and Preferences

Customize your SurviAI experience by configuring your account settings.

### Accessing Account Settings

Click your profile icon in the top right corner and select **"Settings"** from the dropdown menu.

### Profile Information

Update your basic profile details:

- **Display Name** - How you appear in the app
- **Email Address** - For notifications and login
- **Profile Picture** - Upload a custom avatar
- **Organization** - Your company or team name

### Notification Preferences

Control how and when you receive updates:

**Email Notifications**
- New survey responses
- Daily/weekly response summaries
- Product updates and tips
- Account security alerts

**In-App Notifications**
- Real-time response alerts
- System announcements
- Team activity (for team plans)

### Display Preferences

Customize your visual experience:

- **Theme** - Light, Dark, or System default
- **Default View** - Card or List view for projects
- **Language** - Interface language selection
- **Time Zone** - For accurate timestamps

### Security Settings

Keep your account secure:

- **Change Password** - Update regularly
- **Two-Factor Authentication** - Add extra security
- **Active Sessions** - View and manage logged-in devices
- **Login History** - Review recent access

### Connected Accounts

Manage OAuth connections:

- Google account linking
- Microsoft account linking
- Disconnect linked accounts

### Data and Privacy

Control your data:

- **Export Data** - Download all your survey data
- **Delete Account** - Permanently remove your account
- **Privacy Settings** - Manage data sharing preferences

### Subscription Management

For paid plans:

- View current plan details
- Upgrade or downgrade
- Billing history
- Payment methods
        `,
        relatedArticles: [
          { category: "account-billing", slug: "upgrade-plan", title: "Upgrading Your Plan" },
          { category: "account-billing", slug: "billing", title: "Billing and Invoices" },
        ],
      },
    ],
  },
  "survey-creation": {
    id: "survey-creation",
    title: "Survey Creation",
    description: "Learn how to create, edit, and customize your surveys.",
    articles: [
      {
        slug: "supported-formats",
        title: "Supported Document Formats",
        description: "Complete list of document formats supported by SurviAI for AI-powered survey extraction, including PDFs, Word docs, and images.",
        content: `
## Supported Document Formats

SurviAI supports a wide range of document formats for AI-powered question extraction.

### Fully Supported Formats

**Maximum file size for all formats:** 10MB

**PDF Documents (.pdf)**
- Digital PDFs with selectable text
- Scanned PDFs (processed with OCR)
- Multi-page documents

**Microsoft Word (.doc, .docx)**
- Word 97-2003 format (.doc)
- Modern Word format (.docx)
- Preserves formatting for better extraction

**Microsoft Excel (.xls, .xlsx)**
- Spreadsheet-based surveys
- Multiple sheets supported
- Question/answer column detection

**Images (.png, .jpg, .jpeg)**
- Scanned survey forms
- Photos of paper surveys
- OCR text extraction

### Format Recommendations

| Format | Best For | Extraction Quality |
|--------|----------|-------------------|
| DOCX | Structured surveys | Excellent |
| PDF (digital) | Finalized forms | Excellent |
| PDF (scanned) | Paper surveys | Good |
| XLSX | Data-driven surveys | Very Good |
| Images | Quick captures | Good |

### Tips by Format

**For PDFs:**
- Digital PDFs work better than scanned
- Ensure fonts are embedded
- Avoid password protection

**For Word Documents:**
- Use standard fonts
- Avoid text boxes for questions
- Number your questions

**For Excel:**
- Put questions in Column A
- Options in subsequent columns
- Use clear headers

**For Images:**
- High resolution (300 DPI+)
- Good lighting and contrast
- Straight alignment

### Unsupported Formats

Currently not supported:
- PowerPoint (.ppt, .pptx)
- Apple Pages (.pages)
- Google Docs (export to PDF first)
- Plain text files (.txt)
- Handwritten documents
- Audio/Video files
        `,
        relatedArticles: [
          { category: "getting-started", slug: "upload-document", title: "Uploading Your First Document" },
          { category: "survey-creation", slug: "ai-extraction", title: "AI Question Extraction Explained" },
        ],
      },
      {
        slug: "ai-extraction",
        title: "AI Question Extraction Explained",
        description: "Learn how SurviAI's AI technology automatically extracts and classifies survey questions from your documents.",
        content: `
## AI Question Extraction Explained

Understanding how our AI processes your documents helps you get better results.

### How It Works

Our AI extraction uses multiple stages:

**1. Document Analysis**
The AI first analyzes your document structure:
- Identifies text blocks and sections
- Detects formatting patterns
- Recognizes headers and footers
- Maps the document layout

**2. Question Detection**
Using natural language processing, we identify questions by:
- Question marks and interrogative words
- Numbered lists and bullet points
- Survey-specific patterns
- Context and positioning

**3. Type Classification**
Each question is automatically classified from 21 available types:
- Choice questions (single, multiple, dropdown, image)
- Text questions (open text, date/time)
- Scale questions (Likert, star rating, slider)
- Ranking questions (ranking, pick/group/rank)
- Matrix/grid questions (6 varieties)
- Media questions (file upload, video, heat map)

**4. Option Extraction**
For closed-ended questions, we extract:
- Answer choices
- Scale labels
- Matrix headers
- "Other" options

### What the AI Extracts Automatically

- Questions and answer options
- Question types (single choice, multiple choice, etc.)
- Skip logic (e.g., "If No, go to Q5")
- Display conditions (e.g., "Ask only if Q2 = Yes")
- Quota instructions
- Required questions
- Randomization instructions

### Factors Affecting Quality

**Improves extraction:**
- Clear question numbering
- Consistent formatting
- Standard fonts
- Logical organization
- Explicit answer options

**May reduce accuracy:**
- Complex layouts
- Multiple columns
- Mixed languages
- Low image quality
- Unusual fonts

### Review and Edit

Always review AI-extracted content:
- Verify question text is complete
- Check question type assignments
- Confirm answer options
- Add missing elements

### Machine Learning Improvement

Our AI continuously improves through:
- User corrections and feedback
- Pattern learning
- Regular model updates
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "edit-questions", title: "Editing Extracted Questions" },
          { category: "survey-creation", slug: "question-types", title: "Question Types Overview" },
          { category: "troubleshooting", slug: "extraction-issues", title: "AI Extraction Not Working" },
        ],
      },
      {
        slug: "edit-questions",
        title: "Editing Extracted Questions",
        description: "How to review, modify, and perfect AI-extracted survey questions using SurviAI's intuitive editor.",
        content: `
## Editing Extracted Questions

After AI extraction, you can fine-tune your survey using our powerful editor.

### The Survey Editor Layout

The editor is split into two sections:

| Left Side (2/3 of screen) | Right Side (1/3 of screen) |
|---------------------------|----------------------------|
| **Survey Editor**: Edit your questions | **Preview**: See how it looks |
| | **Settings**: Survey title, description |
| | **Actions**: Save, Publish |

### Question Cards

Each question appears as a card with these elements:

| Button | What It Does |
|--------|--------------|
| ⋮⋮ (Drag Handle) | Hold and drag to move the question |
| 📋 (Duplicate) | Create a copy of this question |
| 🗑️ (Delete) | Remove this question |
| 🔀 Logic | Shows if question has skip logic or conditions |

### Editing Question Text

Click on any question card to expand and edit it:

1. **Question Text**: Type your question in the text box
2. **Question Type**: Use the dropdown to change the type
3. **Options**: Add/remove answer choices
4. **Required Toggle**: Switch on to make mandatory
5. **Logic & Conditions**: Add skip logic, display conditions, or segmentation rules

### Changing Question Types

To change a question type:
1. Click the question card to expand it
2. Find the **"Question Type"** dropdown
3. Select the appropriate type
4. Configure type-specific options

**All 21 available types:**

*Choice:* Single Choice, Multiple Choice, Dropdown, Image Choice

*Text:* Open Text, Date/Time

*Scale:* Likert Scale, Rating (Stars), Slider/Range

*Ranking:* Ranking, Pick Group & Rank

*Matrix:* Radio Grid, Checkbox Grid, Dropdown Matrix, Text Matrix, Numeric Matrix, Ranking Matrix

*Media:* File Upload, Video Response, Heat Map

### Managing Answer Options

For choice-based questions:

**Add options:**
- Click **"+ Add Option"**
- Type the new choice
- Press Enter to confirm

**Edit options:**
- Click the option text
- Make your changes
- Click away to save

**Remove options:**
- Hover over the option
- Click the X icon

**Reorder options:**
- Drag the handle icon
- Drop in new position

### Reordering Questions

Reorganize your survey:
- Drag questions by the ⋮⋮ handle
- Drop in the desired position
- Questions renumber automatically

### Adding New Questions

Insert questions not in the original:
1. Click **"+ Add Question"** (or the + between cards)
2. Choose the question type
3. Enter your question text
4. Add options if needed

### Deleting Questions

Remove unwanted questions:
1. Click the question card
2. Click the trash icon (🗑️)
3. Confirm deletion

### Auto-Save

✨ Your changes save automatically!

- Look for "Saved" with a checkmark ✓ at the top
- "Unsaved changes" means it's about to save
- "Saving..." means it's currently saving

### The Preview Panel

The right side shows a live preview:
- Updates in real-time as you edit
- Shows exactly how respondents will see it
- Click **"Open Full Preview"** to test the complete flow
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "add-questions", title: "Adding Custom Questions" },
          { category: "survey-creation", slug: "question-types", title: "Question Types Overview" },
        ],
      },
      {
        slug: "add-questions",
        title: "Adding Custom Questions",
        description: "Learn how to manually add new questions to your survey, choose question types, and configure options.",
        content: `
## Adding Custom Questions

Beyond AI extraction, you can manually add questions to customize your survey.

### Adding a New Question

1. Open your survey in the editor
2. Click the **"+ Add Question"** button (or the + icon between existing questions)
3. Select a question type from the menu
4. Enter your question text

### Question Type Categories

SurviAI offers **21 question types** organized into 6 categories:

---

**Choice Questions**
| Type | Description | Use For |
|------|-------------|---------|
| Single Choice | Radio buttons, pick ONE | Yes/No, preferences, demographics |
| Multiple Choice | Checkboxes, pick MULTIPLE | "Select all that apply" |
| Dropdown | Compact list | Long lists (countries, etc.) |
| Image Choice | Pick from pictures | Product preferences, visual concepts |

---

**Text Questions**
| Type | Description | Use For |
|------|-------------|---------|
| Open Text | Text box for written responses | Feedback, comments, names |
| Date/Time | Calendar/time picker | Scheduling, birthdays, events |

---

**Scale Questions**
| Type | Description | Use For |
|------|-------------|---------|
| Likert Scale | Numbered scale (1-5, 1-7) | Agreement, satisfaction |
| Rating (Stars) | Star rating (1-5) | Quick satisfaction, reviews |
| Slider/Range | Sliding scale between values | Percentages, NPS (0-10) |

---

**Ranking Questions**
| Type | Description | Use For |
|------|-------------|---------|
| Ranking | Drag items to order | Feature prioritization |
| Pick, Group & Rank | Sort items into groups | Consideration, categorization |

---

**Matrix Questions (Grids)**
| Type | Description | Use For |
|------|-------------|---------|
| Radio Grid | ONE option per row | Rating multiple items on same scale |
| Checkbox Grid | MULTIPLE options per row | Selecting attributes for each item |
| Dropdown Matrix | Dropdown per cell | Complex ratings with many options |
| Text Matrix | Text in each cell | Multiple text responses in table |
| Numeric Matrix | Numbers in each cell | Budget allocations, scoring |
| Ranking Matrix | Rank items in each row | Preferences across categories |

---

**Media Questions**
| Type | Description | Use For |
|------|-------------|---------|
| File Upload | Upload PDF, Word, Excel, images (10MB max) | Resumes, documents, photos |
| Video Response | Record video answer | Testimonials, interviews |
| Heat Map | Click on an image | Design feedback, ad testing |

### Configuring Options

After selecting a type:

1. **Add answer options** (for choice questions)
2. **Set scale range** (for rating questions)
3. **Add row/column labels** (for matrix)
4. **Configure the "Other" option** (toggle "Allow Other")

### Question Settings

For each question, configure:

- **Required** - Toggle ON to make mandatory
- **Question Text** - The actual question
- **Options** - Add/edit answer choices
- **Logic** - Skip logic, display conditions, segmentation

### Best Practices

- Use clear, simple language
- Avoid double-barreled questions
- Provide balanced options
- Consider mobile display
- Test before publishing using "Open Full Preview"
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "question-types", title: "Question Types Overview" },
          { category: "survey-creation", slug: "edit-questions", title: "Editing Extracted Questions" },
        ],
      },
      {
        slug: "question-types",
        title: "Question Types Overview",
        description: "Complete guide to all 21 question types available in SurviAI, with examples and best use cases for each.",
        content: `
## Question Types Overview

SurviAI supports **21 different question types** organized into six categories. Here's a complete guide to each one.

---

## Choice Questions

### Single Choice
Respondents pick **ONE** option from a list (radio buttons).

**Use for:** Yes/No questions, gender, age ranges, preferences

**Example:**
> What is your preferred method of communication?
> ○ Email
> ○ Phone
> ○ Text Message
> ○ In Person

### Multiple Choice
Respondents can pick **MULTIPLE** options (checkboxes).

**Use for:** "Select all that apply" questions

**Example:**
> Which social media platforms do you use? (Select all that apply)
> ☐ Facebook
> ☐ Instagram
> ☐ Twitter/X
> ☐ LinkedIn
> ☐ TikTok

### Dropdown
A compact list that opens when clicked.

**Use for:** Long lists (countries, departments, etc.)

**Example:**
> Select your department: [Click to choose ▼]

### Image Choice
Respondents select from pictures instead of text.

**Use for:** Product preferences, visual concepts, design feedback, logo testing

**Features:**
- Single select or multiple select mode
- Optional captions below each image
- Supports "None of the above" option

---

## Text Questions

### Open Text
A text box for written responses.

**Use for:** Feedback, comments, suggestions, names

### Date/Time
A calendar/time picker with three modes:
- Date only
- Time only
- Date & Time

**Use for:** Scheduling, birthdays, event dates

---

## Scale Questions

### Likert Scale
A numbered scale (usually 1-5 or 1-7) with customizable labels.

**Use for:** Agreement levels, satisfaction ratings

**Example:**
> How satisfied are you with our service?
> ① ② ③ ④ ⑤
> Strongly Disagree → Strongly Agree

### Rating (Stars)
Star rating from 1-5 stars.

**Use for:** Quick satisfaction ratings, reviews

### Slider/Range
A sliding scale between two values with customizable min, max, and step.

**Use for:** Percentages, intensity levels, NPS scores (0-10)

---

## Ranking Questions

### Ranking
Drag items to put them in order of preference.

**Example:**
> Rank these features by importance (drag to reorder):
> 1. ≡ Customer Support
> 2. ≡ Product Quality
> 3. ≡ Price
> 4. ≡ Delivery Speed

### Pick, Group & Rank
Respondents drag items from a pool into categorized groups with optional ranking within each group.

**Use for:** Product consideration, priority categorization, feature sorting

**Example:**
> Sort these devices into your consideration categories:
> [Available Items] → [Considering] / [Not Considering]

---

## Matrix Questions (Grids)

Matrix questions show a grid where rows are items and columns are options.

### Radio Grid
Pick ONE option per row.

**Use for:** Rating multiple items on the same scale

### Checkbox Grid
Pick MULTIPLE options per row.

**Use for:** Selecting multiple attributes for each item

### Dropdown Matrix
Select from a dropdown for each cell.

**Use for:** Complex ratings with many options

### Text Matrix
Type text in each cell.

**Use for:** Collecting multiple text responses in a table

### Numeric Matrix
Enter numbers in each cell.

**Use for:** Budget allocations, scoring multiple items

### Ranking Matrix
Rank items in each row.

**Use for:** Comparing preferences across categories

---

## Media Questions

### File Upload
Respondents upload a file (PDF, Word, Excel, or images).

**Maximum file size:** 10MB

**Use for:** Resumes, documents, photos, signed forms

### Video Response
Respondents record a video answer.

**Features:**
- Configurable max duration (30s, 60s, 2min, etc.)
- Allow retakes before final submission

**Use for:** Testimonials, interviews, presentations, qualitative feedback

### Heat Map
Respondents click on an image to mark areas of interest.

**Features:**
- Track up to 10 clicks per image
- Optional regions for categorizing clicks

**Use for:** Design feedback, eye tracking simulation, product packaging tests, ad testing

---

## The "Other" Option

For choice questions, you can enable an "Other" option:
- Toggle **"Allow Other"** ON
- Customize the label (default: "Other (please specify)")
- Respondents can type their own answer

**"Other" supports full survey logic:**
- Set skip/terminate rules on the "Other" option
- Reference "Other" in conditions
- In **Text Only**, exports display as "Other: [user's text]". In **Code and Text**, a configured option code appears with the typed text. In **Code Only**, a configured option code is exported instead of the typed text.
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "add-questions", title: "Adding Custom Questions" },
          { category: "getting-started", slug: "first-survey", title: "Creating Your First Survey" },
        ],
      },
    ],
  },
  "distribution": {
    id: "distribution",
    title: "Distribution",
    description: "Share your surveys and collect responses effectively.",
    articles: [
      {
        slug: "share-link",
        title: "Sharing via Link",
        description: "Learn how to generate and share your unique survey link to collect responses from your target audience.",
        content: `
## Sharing via Link

The easiest way to distribute your survey is with a shareable link.

### Getting Your Survey Link

After publishing your survey:

1. A green box appears showing **"Survey is Live!"**
2. You'll see a text box with your unique survey URL
3. Click the **Copy** button (📋) next to the link
4. The link is now copied to your clipboard!

### Alternative: From the Projects Page

1. Find your published survey (look for the green **"Published"** badge)
2. Click the link icon (🔗) button
3. The share link is copied to your clipboard

### How to Share

**Via Email**
1. Copy the link
2. Open your email
3. Paste the link in your message
4. Send to your recipients

**Via Chat/Messaging**
1. Copy the link
2. Paste into Teams, Slack, WhatsApp, etc.

**In Documents**
1. Copy the link
2. Paste into Word, PowerPoint, or any document

**QR Code** (Coming Soon)
- Generate a QR code for physical distribution
- Perfect for printed materials and presentations

### Link Behavior

Your survey link:
- Works immediately after publishing
- Remains consistent (same URL)
- Stops working if you unpublish the survey
- Can be re-shared at any time

### Troubleshooting Share Links

**Link not working?**
- Make sure the survey is published (not draft)
- Check that the full link was copied
- Try opening the link in an incognito/private window

**Need to take survey offline?**
- Unpublish the survey in the Survey Editor
- The link will stop accepting responses
- You can republish anytime

### Best Practices

- Test your link before sharing widely
- Include context when sharing (what the survey is about)
- Let respondents know how long it takes
- Set expectations for when the survey closes
- Monitor responses in the Responses dashboard
        `,
        relatedArticles: [
          { category: "distribution", slug: "embed-survey", title: "Embedding Surveys on Websites" },
          { category: "distribution", slug: "email-distribution", title: "Email Distribution" },
        ],
      },
      {
        slug: "embed-survey",
        title: "Embedding Surveys on Websites",
        description: "How to include your SurviAI survey on your website or in emails using the share link.",
        content: `
## Embedding Surveys on Websites

You can include your SurviAI survey on websites and in communications using your share link.

### Using the Share Link

The primary way to include your survey on websites is via your unique survey URL:

1. Publish your survey
2. Copy the share link from the "Survey is Live!" box
3. Link to it from your website or embed it in an iframe

### Basic Iframe Embed

To display your survey directly on a webpage:

\`\`\`html
<iframe 
  src="https://your-survey-link-here"
  width="100%"
  height="700"
  frameborder="0">
</iframe>
\`\`\`

> **Note:** Replace the src with your actual survey link.

### Linking from Your Website

For a simpler approach, add a button or text link:

\`\`\`html
<a href="https://your-survey-link-here" target="_blank">
  Take Our Survey
</a>
\`\`\`

### Including in Emails

**Best practice for emails:**
1. Copy your survey link
2. Add a clear call-to-action button or text
3. Include context about survey length and purpose

Example email text:
> "We'd love your feedback! Please take our 5-minute survey: [Take the Survey]"

### Tips for Website Integration

**Iframe sizing:**
- Use width="100%" for responsive design
- Set height to at least 600-700px for comfortable scrolling
- Test on mobile devices

**Link placement:**
- Make the survey link prominent
- Include on relevant pages (contact, feedback, post-purchase)
- Consider popup/modal triggers for targeted collection

### Mobile Considerations

SurviAI surveys are mobile-responsive, so:
- Respondents can complete on any device
- No special mobile configuration needed
- The survey automatically adjusts to screen size

### Troubleshooting

**Survey not loading in iframe?**
- Verify the survey is published
- Check that you copied the full URL
- Test the link directly in a browser first

**Display issues?**
- Increase iframe height if content is cut off
- Remove frameborder for cleaner appearance
- Check for CSS conflicts on your page
        `,
        relatedArticles: [
          { category: "distribution", slug: "share-link", title: "Sharing via Link" },
          { category: "distribution", slug: "survey-access", title: "Managing Survey Access" },
        ],
      },
      {
        slug: "email-distribution",
        title: "Email Distribution",
        description: "Best practices for distributing your surveys via email to maximize response rates and engagement.",
        content: `
## Email Distribution

Email remains one of the most effective ways to distribute surveys. Here's how to do it right.

### Preparing Your Survey Link

Before emailing:
1. Ensure your survey is published
2. Copy your survey link
3. Test the link yourself

### Writing Effective Survey Emails

**Subject Line Tips**
- Keep it short (40-50 characters)
- Be clear about the ask
- Create urgency if appropriate
- Personalize when possible

Examples:
- "Quick 2-minute survey: Help us improve"
- "Your feedback matters, [Name]"
- "Survey: What features do you want next?"

**Email Body Best Practices**

1. **Greeting** - Personalize with name
2. **Purpose** - Explain why you're asking
3. **Time estimate** - "Takes only 3 minutes"
4. **Incentive** - If offering one
5. **CTA** - Clear button or link
6. **Deadline** - When it closes
7. **Privacy note** - How data is used

### Email Template Example

> Subject: Quick survey: Help us serve you better
>
> Hi [Name],
>
> We're always looking to improve, and your input is invaluable.
>
> Would you take 3 minutes to share your thoughts in our quick survey?
>
> [Take the Survey]
>
> Your responses are anonymous and will directly influence our roadmap.
>
> Survey closes Friday, [Date].
>
> Thank you for being part of our community!
>
> Best,
> [Your name]

### Timing Your Emails

Best times to send:
- Tuesday-Thursday
- Mid-morning (9-11 AM)
- Early afternoon (1-3 PM)
- Avoid Mondays and Fridays

### Follow-Up Strategy

For better response rates:
1. **Initial invite** - Day 1
2. **First reminder** - Day 3-4
3. **Final reminder** - Day 6-7

Keep reminders shorter and more urgent.

### Tracking Performance

Monitor your email campaign:
- Open rate
- Click-through rate
- Survey completion rate
- Drop-off points

### Avoiding Spam Filters

Ensure delivery:
- Use authenticated sender domain
- Avoid spam trigger words
- Include unsubscribe option
- Maintain clean email list
        `,
        relatedArticles: [
          { category: "distribution", slug: "share-link", title: "Sharing via Link" },
          { category: "responses-analytics", slug: "view-responses", title: "Viewing Survey Responses" },
        ],
      },
      {
        slug: "survey-access",
        title: "Managing Survey Access",
        description: "Control who can access your surveys by publishing, unpublishing, and using quotas to limit responses.",
        content: `
## Managing Survey Access

Control who can view and respond to your surveys through publishing and quota settings.

### Survey States

| State | Description |
|-------|-------------|
| **Draft** | Survey is being edited, not accessible to respondents |
| **Published** | Survey is live and accepting responses |
| **Unpublished** | Survey was live but is now paused |

### Publishing a Survey

When your survey is ready:

1. In the Survey Editor, look at the right panel
2. Find the **"Publish Survey"** button (purple with rocket icon 🚀)
3. Click the button
4. Wait for confirmation: "🎉 Survey Published!"
5. Copy your unique share link from the green "Survey is Live!" box

### Unpublishing a Survey

Need to take your survey offline?

1. In the Survey Editor, find the **"Unpublish Survey"** button
2. Click it to return the survey to draft mode
3. The share link will stop working
4. You can republish anytime

### Controlling Responses with Quotas

Use quotas to limit how many responses you collect:

**Setting Up Quotas:**
1. Enable quotas in the Survey Editor
2. Define segments (e.g., by age, gender, region)
3. Set target numbers for each segment
4. When a segment is full, new respondents in that segment are screened out

**Quota Behavior:**
- When a segment reaches its target, it automatically closes
- New respondents matching that segment see a termination screen
- Other segments continue accepting responses

### Response Termination

Respondents may be terminated for:

| Reason | What Happens |
|--------|--------------|
| **Screen Out** | Didn't qualify based on their answers |
| **Quota Full** | Their segment has reached its target |

### Best Practices

- Test your survey before publishing using "Open Full Preview"
- Review all skip logic paths to ensure proper flow
- Monitor the Quota Dashboard to track segment fill rates
- Unpublish temporarily if you need to make changes (though edits apply immediately)

### Troubleshooting

**Link not working?**
- Check that the survey is published (not in draft mode)
- Ensure the full link was copied
- Try opening in an incognito/private window

**Respondents being screened out unexpectedly?**
- Check your quota settings
- Review skip logic with termination
- Verify display conditions aren't hiding required questions
        `,
        relatedArticles: [
          { category: "distribution", slug: "share-link", title: "Sharing via Link" },
          { category: "responses-analytics", slug: "filter-responses", title: "Response Filtering and Segmentation" },
        ],
      },
    ],
  },
  "responses-analytics": {
    id: "responses-analytics",
    title: "Responses & Analytics",
    description: "Analyze responses and generate insights from your data.",
    articles: [
      {
        slug: "view-responses",
        title: "Viewing Survey Responses",
        description: "Learn how to view, analyze, and manage individual and aggregate survey responses in SurviAI.",
        content: `
## Viewing Survey Responses

Access and analyze your collected survey data.

### Accessing Responses

**From the Survey Editor:**
1. Open your published survey
2. Click **"View Responses"** button (purple with people icon)

**From the Projects Page:**
1. Find your published survey (green "Published" badge)
2. Click the people icon (👥) button

### The Responses Dashboard

At the top, you'll see summary statistics:

| Stat | What It Shows |
|------|---------------|
| **Total Responses** | How many people started the survey |
| **Completed** | How many finished the entire survey |
| **Completion Rate** | Percentage who finished |
| **Screen Outs** | How many were terminated (quota full, didn't qualify) |

### Response Table

Below the stats, you'll see a table with:

| Column | Description |
|--------|-------------|
| **#** | Response number |
| **Submitted** | Date and time |
| **Status** | Complete, Partial, or Terminated |
| **Termination Type** | Why they were terminated (if applicable) |
| **Segment** | Which quota segment (if tracked) |
| **Preview Columns** | First few answers |
| **Actions** | View full response |

### Understanding Response Status

| Status | Meaning |
|--------|---------|
| **Complete** | Finished all questions and submitted |
| **Partial** | Started but didn't finish |
| **Terminated - Screen Out** | Didn't qualify based on answers |
| **Terminated - Quota Full** | Their segment quota was full |

### Viewing Individual Responses

1. Find the response in the table
2. Click **"View"** button on the right
3. A popup shows all answers for that response
4. Scroll through to see every question and answer
5. For media questions, click to view files/videos

### Viewing Uploaded Files

For file upload and video response questions:
1. Click the file icon in the response
2. Files download or open in a new tab
3. Videos play in an embedded player

### Searching Responses

Looking for a specific response?

1. Use the search box at the top of the responses table
2. Type any text to search (name, date, answer content)
3. Results filter in real-time
4. Press "/" on your keyboard for quick access to search

### Pagination

For surveys with many responses:
- Use the page numbers at the bottom to navigate
- Change "Show per page" to see more at once (10, 25, 50, or 100)
- Use "Go to" to jump to a specific page
        `,
        relatedArticles: [
          { category: "responses-analytics", slug: "analytics-dashboard", title: "Understanding Analytics Dashboards" },
          { category: "responses-analytics", slug: "export-data", title: "Exporting Response Data" },
        ],
      },
      {
        slug: "analytics-dashboard",
        title: "Understanding Analytics Dashboards",
        description: "Navigate the responses and quota dashboards to gain insights from your survey data.",
        content: `
## Understanding Analytics Dashboards

SurviAI provides two main dashboards for monitoring your survey data: the **Responses Dashboard** and the **Quota Dashboard**.

### Responses Dashboard

Access by clicking "View Responses" on any published survey. At the top, you'll see summary statistics:

| Stat | What It Shows |
|------|---------------|
| **Total Responses** | How many people started the survey |
| **Completed** | How many finished the entire survey |
| **Completion Rate** | Percentage who finished |
| **Screen Outs** | How many were terminated (quota full, didn't qualify) |

### Response Table

The main section shows a table with all responses:

| Column | Description |
|--------|-------------|
| **#** | Response number |
| **Submitted** | Date and time |
| **Status** | Complete, Partial, or Terminated |
| **Termination Type** | Why they were terminated (if applicable) |
| **Segment** | Which quota segment (if tracked) |
| **Preview Columns** | First few answers |
| **Actions** | View full response |

### Quota Dashboard

If you've enabled quotas for segmentation, access the Quota Dashboard by clicking the pie chart icon (📊) on your survey.

**The Quota Dashboard Shows:**

| Element | Description |
|---------|-------------|
| **Overall Progress** | How close you are to completing all quotas |
| **Segment Breakdown** | Each segment's name, target, and current count |
| **Progress Bars** | Visual fill showing completion |
| **Status Indicators** | Open (accepting), Full (closed), or Paused |

### Understanding Quota Status

| Status | Meaning |
|--------|---------|
| **Open** | Still accepting responses for this segment |
| **Full** | Target reached, new respondents in this segment will be terminated |
| **Paused** | Temporarily not accepting responses |

### Key Metrics to Monitor

**Response Health**
- Are responses coming in at expected rate?
- Is completion rate acceptable?
- Are screen-out rates too high?

**Quota Balance**
- Are all segments filling evenly?
- Any segments filling too fast or slow?
- Need to adjust targets?

### Exporting for Analysis

For deeper analysis, export your data to CSV:
1. Click the green **"Export"** button
2. Choose your export options
3. Open in Excel, Google Sheets, or statistical software

See [Exporting Response Data](/help/responses-analytics/export-data) for details.
        `,
        relatedArticles: [
          { category: "responses-analytics", slug: "export-data", title: "Exporting Response Data" },
          { category: "responses-analytics", slug: "filter-responses", title: "Response Filtering and Segmentation" },
        ],
      },
      {
        slug: "export-data",
        title: "Exporting Response Data",
        description: "How to export your survey responses to CSV format for external analysis, including option code export.",
        content: `
## Exporting Response Data

Download your survey data for analysis in Excel, Google Sheets, or statistical software.

### How to Export

1. Open your survey's Responses page
2. Click the green **"Export"** button (top right)
3. An options dialog appears
4. Choose your settings (see below)
5. Click **"Export"**
6. Your file downloads automatically

### Export Format

Currently, SurviAI exports to **CSV** (Comma-Separated Values):
- Universal compatibility
- Opens in Excel, Google Sheets, SPSS
- UTF-8 encoding preserves special characters

### Export Options

| Option | Choices | When to Use |
|--------|---------|-------------|
| **Answer Format** | Text Only / Code Only / Code and Text | How choice answers appear |
| **Matrix Format** | Compact / Expanded | How grid questions appear |
| **Include Metadata** | Yes/No | Adds device info |
| **Include Timestamps** | Yes/No | Adds start and end times |

### Answer Format Options

Answer options can have **codes** (like "1", "2", "A", "B") for data analysis, whether they are imported with a survey or configured in **Option-Level Logic**. SurviAI lets you choose how to export them.

**Text Only** (Default):
- Exports the full text of each answer
- Example: "Strongly Agree"
- Best for: Reports, presentations, human-readable exports

**Code Only**:
- Exports just the numeric or letter code
- Example: "1"
- Best for: Statistical software (SPSS, R), data processing

**Code and Text**:
- Exports both code and text together
- Example: "1. Strongly Agree"
- Best for: Cross-referencing, documentation, audit trails

**Example Comparison:**

| Question | Text Only | Code Only | Code and Text |
|----------|-----------|-----------|---------------|
| Q1: Gender | Male | 1 | 1. Male |
| Q2: Satisfaction | Very Satisfied | 5 | 5. Very Satisfied |
| Q3: Brand | Brand A | A | A. Brand A |

> 💡 **Tip**: If no option code was defined, the export falls back to showing the text.

> 📝 **Note**: Answer format applies to single choice, multiple choice, dropdown, and ranking questions. Other question types (text, rating, matrix) are unaffected.

> ⚠️ **Caution**: When the "Other" option has a code, **Code Only** exports that code (by default, "OTHER") without the respondent's typed text. Use **Text Only** or **Code and Text** when you need to retain "Other" verbatims.

### Matrix Format Options

**Compact** (Default):
- One column per matrix question
- Answers formatted as "Row1: Option; Row2: Option"
- Good for: Quick overview, smaller files

**Expanded**:
- One column per row in the matrix
- Each row item gets its own column
- Good for: Statistical analysis, pivot tables

### Understanding the Export File

**Standard Columns:**
- Response ID
- Submitted At
- Status (Complete/Partial/Terminated)
- Termination Type (if applicable)
- Segment (if quotas enabled)

**Question Columns:**
- Each question becomes a column
- Column headers: "Q1: Question text", "Q2: Question text"...
- For eligible choice questions, answers use the selected option text, code, or both according to **Answer Format**

**Special Question Type Formats:**

| Question Type | Export Format |
|---------------|---------------|
| Single Choice | Depends on Answer Format setting |
| Multiple Choice | Semicolon-separated; respects Answer Format |
| Ranking | Semicolon-separated in rank order; respects Answer Format |
| Dropdown | Depends on Answer Format setting |
| Matrix/Grid | Row: Column format or expanded |
| Heat Map | Click coordinates: "Click 1: (x, y) [Region]" |
| Pick Group Rank | Group: Items format |
| File Upload | [File: filename.pdf] |
| Video Response | [Video: filename.webm] |

### Opening in Excel

1. Find the downloaded .csv file
2. Double-click to open in Excel
3. Each row = one response
4. Each column = one question

### Opening in Google Sheets

1. Go to sheets.google.com
2. File → Import → Upload
3. Select your .csv file
4. Click "Import data"
        `,
        relatedArticles: [
          { category: "responses-analytics", slug: "view-responses", title: "Viewing Survey Responses" },
          { category: "responses-analytics", slug: "analytics-dashboard", title: "Understanding Analytics Dashboards" },
        ],
      },
      {
        slug: "filter-responses",
        title: "Response Filtering and Segmentation",
        description: "Search and filter your survey responses to find specific data and understand your audience segments.",
        content: `
## Response Filtering and Segmentation

SurviAI provides tools to search your responses and view data by segment.

### Searching Responses

Looking for a specific response?

1. Use the search box at the top of the responses table
2. Type any text to search (name, date, answer content)
3. Results filter in real-time
4. Press "/" on your keyboard for quick access to search

**Search Tips:**
- Search for specific answer text
- Search by date (e.g., "Feb 12")
- Search for partial matches

### Pagination for Large Datasets

For surveys with many responses:
- Use the page numbers at the bottom to navigate
- Change "Show per page" to see more at once (10, 25, 50, or 100)
- Use "Go to" to jump to a specific page

### Understanding Response Status

Use status to understand your data:

| Status | Meaning |
|--------|---------|
| **Complete** | Finished all questions and submitted |
| **Partial** | Started but didn't finish |
| **Terminated - Screen Out** | Didn't qualify based on answers |
| **Terminated - Quota Full** | Their segment quota was full |

### Quota-Based Segmentation

If you've set up quotas with segments, the **Segment** column shows which group each respondent belongs to. This allows you to:

- See how many responses per segment
- Identify which segments are filling fastest
- Understand your audience composition

### Viewing by Segment

On the Quota Dashboard:
1. See each segment's name, target, and current count
2. Progress bars show visual completion
3. Status indicators show Open, Full, or Paused

### Exporting Filtered Data

To analyze specific data outside SurviAI:

1. Use search to find relevant responses
2. Click **"Export"** to download to CSV
3. Open in Excel or Google Sheets
4. Use spreadsheet filters for deeper analysis

### Tips for Effective Analysis

- Export to Excel for cross-tabulation and pivot tables
- Use "Code Only" export for statistical software
- Compare segments by exporting all data and filtering in your spreadsheet
- Look for patterns in termination types to optimize your survey
        `,
        relatedArticles: [
          { category: "responses-analytics", slug: "view-responses", title: "Viewing Survey Responses" },
          { category: "responses-analytics", slug: "analytics-dashboard", title: "Understanding Analytics Dashboards" },
        ],
      },
    ],
  },
  "account-billing": {
    id: "account-billing",
    title: "Account & Billing",
    description: "Manage your account settings and subscription.",
    articles: [
      {
        slug: "upgrade-plan",
        title: "Upgrading Your Plan",
        description: "How to upgrade your SurviAI subscription to access more features, higher limits, and premium capabilities.",
        content: `
## Upgrading Your Plan

Unlock more features and higher limits by upgrading your SurviAI plan.

### Current Plans

**Free Plan**
- 3 active surveys
- 100 responses per survey
- Basic question types
- CSV export
- Community support

**Pro Plan**
- Unlimited surveys
- Unlimited responses
- All question types
- Advanced logic
- Priority support
- Custom branding
- API access

**Enterprise**
- Everything in Pro
- Dedicated account manager
- SSO/SAML integration
- Custom contracts
- SLA guarantees
- On-premise options

### How to Upgrade

1. Click your profile icon
2. Select **"Billing"**
3. Click **"Upgrade Plan"**
4. Choose your plan
5. Enter payment details
6. Confirm upgrade

### Immediate Benefits

When you upgrade:
- Features unlock instantly
- Limits increase immediately
- Existing surveys gain new features
- No data migration needed

### Payment Options

**Monthly Billing**
- Pay month-to-month
- Cancel anytime
- More flexibility

**Annual Billing**
- Pay yearly
- Save up to 20%
- Best value

**Payment Methods**
- Credit/debit cards
- PayPal
- Invoice (Enterprise only)

### Proration

If upgrading mid-cycle:
- Pay difference only
- Credit applied automatically
- No double billing

### Team Plans

For multiple users:
- Per-seat pricing available
- Centralized billing
- Admin controls
- Contact sales for details

### Downgrading

If you need to downgrade:
1. Go to Billing settings
2. Click **"Change Plan"**
3. Select lower tier
4. Confirm changes

Note: Feature access changes at next billing cycle.

### Cancellation

To cancel your subscription:
1. Go to Billing
2. Click **"Cancel Subscription"**
3. Complete exit survey
4. Confirm cancellation

You retain access until the end of your billing period.
        `,
        relatedArticles: [
          { category: "account-billing", slug: "billing", title: "Billing and Invoices" },
          { category: "account-billing", slug: "team-management", title: "Managing Team Members" },
        ],
      },
      {
        slug: "team-management",
        title: "Managing Team Members",
        description: "Invite team members, assign roles, and collaborate on surveys with your organization.",
        content: `
## Managing Team Members

Collaborate with your team by inviting members and managing permissions.

### Accessing Team Settings

1. Click your profile icon
2. Select **"Team Settings"**
3. View current team members

### Inviting Team Members

1. Click **"Invite Member"**
2. Enter their email address
3. Select their role
4. Click **"Send Invite"**

They'll receive an email with instructions to join.

### Team Roles

**Owner**
- Full account access
- Billing management
- Can delete account
- Cannot be removed

**Admin**
- All survey permissions
- Team management
- Cannot access billing
- Can be removed

**Editor**
- Create and edit surveys
- View all responses
- Cannot manage team
- Cannot change settings

**Viewer**
- View surveys only
- View responses
- Cannot make changes
- Limited access

### Role Permissions Matrix

| Permission | Owner | Admin | Editor | Viewer |
|------------|-------|-------|--------|--------|
| Create surveys | ✓ | ✓ | ✓ | ✗ |
| Edit surveys | ✓ | ✓ | ✓ | ✗ |
| Delete surveys | ✓ | ✓ | ✓ | ✗ |
| View responses | ✓ | ✓ | ✓ | ✓ |
| Export data | ✓ | ✓ | ✓ | ✗ |
| Manage team | ✓ | ✓ | ✗ | ✗ |
| Billing access | ✓ | ✗ | ✗ | ✗ |

### Changing Roles

To change a member's role:
1. Find them in the team list
2. Click the role dropdown
3. Select new role
4. Confirm change

### Removing Members

To remove a team member:
1. Find them in the list
2. Click **"Remove"**
3. Confirm removal

Their access is revoked immediately.

### Pending Invitations

View and manage pending invites:
- Resend invitation
- Cancel invitation
- Change invited role

### Team Activity

Track team actions:
- Login history
- Recent changes
- Survey activity
- Export audit log
        `,
        relatedArticles: [
          { category: "account-billing", slug: "upgrade-plan", title: "Upgrading Your Plan" },
          { category: "getting-started", slug: "account-setup", title: "Account Setup and Preferences" },
        ],
      },
      {
        slug: "billing",
        title: "Billing and Invoices",
        description: "View your billing history, download invoices, update payment methods, and manage your subscription.",
        content: `
## Billing and Invoices

Manage all aspects of your SurviAI subscription billing.

### Accessing Billing

1. Click your profile icon
2. Select **"Billing"**
3. View your billing dashboard

### Billing Dashboard

Your dashboard shows:
- Current plan
- Next billing date
- Payment method on file
- Usage statistics

### Viewing Invoices

All invoices are available:
1. Go to **"Invoice History"**
2. View list of invoices
3. Click to see details
4. Download as PDF

Invoice details include:
- Invoice number
- Billing period
- Items and amounts
- Payment status
- Tax information

### Updating Payment Method

To update your card:
1. Go to **"Payment Methods"**
2. Click **"Update Card"**
3. Enter new card details
4. Click **"Save"**

Supported methods:
- Visa
- Mastercard
- American Express
- PayPal

### Billing Cycle

Your billing cycle:
- Starts on signup date
- Renews automatically
- Can view next charge date
- Receipts sent via email

### Failed Payments

If payment fails:
1. Email notification sent
2. 3-day grace period
3. Retry automatically
4. Update card if needed

After multiple failures:
- Account may be downgraded
- Data is preserved
- Upgrade to restore access

### Tax Information

For business accounts:
- Add VAT/Tax ID
- Update billing address
- Download tax invoices
- Request W-9 forms

### Usage-Based Billing

If on usage plan:
- View current usage
- See projected costs
- Set usage alerts
- Review detailed breakdown

### Refunds

Refund policy:
- Pro-rated for annual plans
- Within 30 days of charge
- Contact support to request
- Processing takes 5-10 days
        `,
        relatedArticles: [
          { category: "account-billing", slug: "upgrade-plan", title: "Upgrading Your Plan" },
          { category: "account-billing", slug: "cancellation", title: "Cancellation and Refunds" },
        ],
      },
      {
        slug: "cancellation",
        title: "Cancellation and Refunds",
        description: "How to cancel your SurviAI subscription, understand what happens to your data, and request refunds.",
        content: `
## Cancellation and Refunds

Everything you need to know about canceling your SurviAI subscription.

### How to Cancel

1. Go to **Settings** → **Billing**
2. Scroll to **"Cancel Subscription"**
3. Click the cancel button
4. Complete the exit survey (optional)
5. Confirm cancellation

### What Happens When You Cancel

**Immediate:**
- Cancellation confirmed
- Email confirmation sent
- No future charges

**End of Billing Period:**
- Account downgrades to Free
- Premium features disabled
- Survey limits applied

**Your Data:**
- All data preserved
- Surveys remain accessible
- Responses kept intact
- Can export anytime

### Access After Cancellation

You keep access to:
- Free plan features
- Your existing surveys
- All response data
- Export functionality

You lose access to:
- Premium question types
- Advanced analytics
- Team collaboration
- Priority support

### Reactivating Your Account

Changed your mind?
1. Go to Billing
2. Click **"Reactivate"**
3. Confirm payment method
4. Full access restored

All your data and settings are preserved.

### Refund Policy

**Monthly Plans:**
- No partial refunds
- Cancel before renewal
- Use until period ends

**Annual Plans:**
- Pro-rated refunds available
- Within 30 days: full refund
- After 30 days: credit applied
- Contact support to request

### Requesting a Refund

1. Contact support@survi.ai
2. Include account email
3. Explain reason briefly
4. Allow 5-10 business days

### Account Deletion

To permanently delete your account:
1. Export all your data first
2. Go to Settings → Account
3. Click **"Delete Account"**
4. Confirm deletion

**Warning:** This action is irreversible. All data will be permanently deleted.

### Pausing Instead

Need a break? Consider:
- Downgrade to Free plan
- Keep your data
- Upgrade when ready
- No deletion necessary
        `,
        relatedArticles: [
          { category: "account-billing", slug: "billing", title: "Billing and Invoices" },
          { category: "getting-started", slug: "account-setup", title: "Account Setup and Preferences" },
        ],
      },
    ],
  },
  "troubleshooting": {
    id: "troubleshooting",
    title: "Troubleshooting",
    description: "Solutions to common issues and problems.",
    articles: [
      {
        slug: "upload-issues",
        title: "Document Upload Issues",
        description: "Troubleshoot common problems with document uploads including file errors, processing failures, and format issues.",
        content: `
## Document Upload Issues

Having trouble uploading documents? Here are solutions to common problems.

### Upload Fails Immediately

**Check file size**
- Maximum: 10MB for all file types

**Check file format**
- Supported: PDF, DOCX, DOC, XLSX, XLS, PNG, JPG, JPEG
- Not supported: PPT, Pages, Google Docs, TXT

**Try these steps:**
1. Refresh the page
2. Try a different browser
3. Clear browser cache
4. Disable browser extensions

### Upload Stuck or Slow

**Check your connection**
- Test internet speed
- Try different network
- Disable VPN temporarily

**File may be complex**
- Large files take longer
- Many images slow processing
- Try splitting the document

### File Format Errors

**"Invalid file format"**
- Verify file extension
- Try re-saving the file
- Convert to different format

**PDF Issues**
- Ensure not password-protected
- Try "Print to PDF" to create new version
- Check PDF isn't corrupted

### Processing Fails

**After upload, processing errors:**

1. **Document too complex**
   - Simplify formatting
   - Remove unnecessary images
   - Split into smaller files

2. **Poor scan quality**
   - Re-scan at higher DPI
   - Improve lighting
   - Ensure document is flat

3. **Unusual encoding**
   - Save as UTF-8
   - Try different format
   - Re-export from source

### Browser-Specific Issues

**Chrome**
- Clear cache and cookies
- Disable extensions
- Try incognito mode

**Safari**
- Enable JavaScript
- Check popup blockers
- Try different browser

**Firefox**
- Clear recent history
- Disable tracking protection
- Check permissions

### Still Having Issues?

If problems persist:
1. Note the error message
2. Note the file type and size
3. Contact support@survi.ai
4. Attach the problem file (if not sensitive)
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "supported-formats", title: "Supported Document Formats" },
          { category: "troubleshooting", slug: "extraction-issues", title: "AI Extraction Not Working" },
        ],
      },
      {
        slug: "extraction-issues",
        title: "AI Extraction Not Working",
        description: "Fix issues with AI question extraction, including no questions detected, wrong types, or incomplete extraction.",
        content: `
## AI Extraction Not Working

When AI extraction doesn't work as expected, try these solutions.

### No Questions Detected

**Common causes:**

1. **Poor document quality**
   - Text not readable
   - Low resolution scan
   - Image too dark/light

2. **Unusual formatting**
   - Questions not clearly formatted
   - Missing question marks
   - Complex layouts

3. **Content type**
   - Document isn't a survey
   - Questions embedded in paragraphs
   - No clear structure

**Solutions:**
- Re-scan at higher quality
- Format questions more clearly
- Number your questions
- Add clear answer options

### Wrong Question Types Detected

**AI assigned incorrect types:**

This happens when:
- Answer format is ambiguous
- Options aren't clearly listed
- Scale isn't obvious

**To fix:**
1. Use the editor to change types
2. Update your source document
3. Re-upload with clearer formatting

### Incomplete Extraction

**Missing questions or options:**

1. **Check source document**
   - Is text selectable?
   - Are all pages included?
   - Is anything cut off?

2. **Document complexity**
   - Multi-column layouts
   - Text in images
   - Unusual fonts

3. **Try:**
   - Export to different format
   - Simplify layout
   - Process in sections

### Text is Garbled

**Extraction shows strange characters:**

1. **Encoding issues**
   - Re-save with UTF-8 encoding
   - Try different export settings

2. **Font issues**
   - Embed fonts in PDF
   - Use standard fonts
   - Convert to plain text

### Scanned Document Problems

**OCR not working well:**

1. **Improve scan quality**
   - 300 DPI minimum
   - Good lighting
   - High contrast

2. **Document preparation**
   - Straighten pages
   - Remove staples/bindings
   - Clean scanner glass

3. **Try:**
   - Scan in grayscale
   - Increase contrast
   - Use auto-enhance

### After Extraction

Remember you can always:
- Manually edit any question
- Add missing questions
- Delete incorrect ones
- Change all question types

The AI extraction is a starting point, not the final result.
        `,
        relatedArticles: [
          { category: "survey-creation", slug: "ai-extraction", title: "AI Question Extraction Explained" },
          { category: "survey-creation", slug: "edit-questions", title: "Editing Extracted Questions" },
        ],
      },
      {
        slug: "survey-loading",
        title: "Survey Not Loading",
        description: "Troubleshoot surveys that won't load, display errors, or show blank pages for you or your respondents.",
        content: `
## Survey Not Loading

When your survey won't load properly, follow these troubleshooting steps.

### For Survey Creators

**Survey won't open in editor:**

1. **Refresh the page**
   - Press Ctrl+F5 (Cmd+Shift+R on Mac)
   - Clear browser cache

2. **Check your internet**
   - Test other websites
   - Try different network

3. **Browser issues**
   - Try different browser
   - Disable extensions
   - Clear cookies

4. **Account issues**
   - Log out and back in
   - Check subscription status

### For Respondents

**Survey link shows error:**

1. **Link issues**
   - Check link is complete
   - Ensure no extra spaces
   - Try copying link again

2. **Survey status**
   - Survey may be closed
   - May have reached response limit
   - Creator may have paused it

3. **Browser problems**
   - Enable JavaScript
   - Allow cookies
   - Try different browser

### Common Error Messages

**"Survey not found"**
- Link may be incorrect
- Survey may be deleted
- Check with survey creator

**"Survey is closed"**
- Response period ended
- Response limit reached
- Creator closed it manually

**"Access denied"**
- May require password
- May need email verification
- Check access requirements

### Blank Page Issues

**Survey loads but shows nothing:**

1. **JavaScript blocked**
   - Enable JavaScript
   - Disable script blockers

2. **CSS issues**
   - Clear cache
   - Try incognito mode

3. **Ad blockers**
   - Disable ad blocker
   - Whitelist survi.ai

### Mobile Issues

**Not loading on phone:**

1. **Browser support**
   - Use Chrome or Safari
   - Update browser app

2. **Connection**
   - Try WiFi vs cellular
   - Check signal strength

3. **Device issues**
   - Close other apps
   - Restart browser
   - Clear app cache

### Embedded Survey Issues

**Survey not loading on website:**

1. **Check embed code**
   - Code is complete
   - Proper formatting
   - HTTPS compatibility

2. **Website conflicts**
   - JavaScript conflicts
   - CSS overrides
   - iframe restrictions

3. **Content Security Policy**
   - Whitelist survi.ai
   - Check console errors
        `,
        relatedArticles: [
          { category: "distribution", slug: "share-link", title: "Sharing via Link" },
          { category: "troubleshooting", slug: "browser-support", title: "Browser Compatibility" },
        ],
      },
      {
        slug: "browser-support",
        title: "Browser Compatibility",
        description: "Supported browsers, known issues, and recommendations for the best SurviAI experience.",
        content: `
## Browser Compatibility

Ensure the best SurviAI experience by using a supported browser.

### Fully Supported Browsers

**Google Chrome**
- Version 90+
- Best overall experience
- All features supported
- Recommended for creators

**Mozilla Firefox**
- Version 88+
- Full functionality
- Good performance
- Privacy features work

**Microsoft Edge**
- Version 90+
- Chromium-based
- Full support
- Windows integration

**Safari**
- Version 14+
- Full macOS support
- iOS support
- Some minor limitations

### Mobile Browsers

**iOS Safari**
- iPhone and iPad
- Full respondent support
- Touch optimized
- Version 14+

**Chrome Mobile**
- Android and iOS
- Full functionality
- Best mobile experience
- Latest version recommended

**Samsung Internet**
- Android support
- Good compatibility
- Version 14+

### Not Supported

**Internet Explorer**
- Not supported
- Please upgrade to Edge
- Security risks

**Older browser versions**
- May have issues
- Missing features
- Update recommended

### Browser Settings Required

**JavaScript**
- Must be enabled
- Required for all features
- Check browser settings

**Cookies**
- Required for login
- Used for session management
- Enable third-party if embedding

**Local Storage**
- Used for preferences
- Enables offline features
- Usually enabled by default

### Recommended Settings

For best experience:

1. **Enable JavaScript**
2. **Allow cookies from survi.ai**
3. **Disable aggressive ad blockers**
4. **Keep browser updated**
5. **Clear cache periodically**

### Known Issues

**Safari:**
- Date picker styling differences
- Minor animation variations

**Firefox:**
- Drag-drop may require click-hold
- Print styles vary slightly

**Mobile:**
- Some advanced features limited
- Optimized for portrait mode

### Troubleshooting Browser Issues

1. **Clear cache and cookies**
2. **Disable extensions temporarily**
3. **Try incognito/private mode**
4. **Update browser to latest**
5. **Try different browser**

### System Requirements

**Minimum:**
- 2GB RAM
- Stable internet (1 Mbps+)
- Screen resolution 1024x768

**Recommended:**
- 4GB+ RAM
- Fast internet (10 Mbps+)
- Screen resolution 1280x720+
        `,
        relatedArticles: [
          { category: "troubleshooting", slug: "survey-loading", title: "Survey Not Loading" },
          { category: "troubleshooting", slug: "upload-issues", title: "Document Upload Issues" },
        ],
      },
    ],
  },
};

// Helper function to get article by category and slug
export function getArticle(categoryId: string, slug: string): HelpArticle | null {
  const category = helpArticles[categoryId];
  if (!category) return null;
  return category.articles.find((article) => article.slug === slug) || null;
}

// Helper function to get category by ID
export function getCategory(categoryId: string): HelpCategory | null {
  return helpArticles[categoryId] || null;
}

// Get all articles as flat array for sitemap generation
export function getAllArticles(): { category: string; slug: string; title: string }[] {
  const articles: { category: string; slug: string; title: string }[] = [];
  Object.entries(helpArticles).forEach(([categoryId, category]) => {
    category.articles.forEach((article) => {
      articles.push({
        category: categoryId,
        slug: article.slug,
        title: article.title,
      });
    });
  });
  return articles;
}
